import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MODEL_SETTINGS_CHANGED_EVENT, loadModelSettings, updateModelSettings } from '../../core/settings';
import type { ModelSettings } from '../../core/types';
import { sheetSpring, useMotionPresets } from '../../design/motion';
import { useComponentMeta } from '../../ui';
import { chatComponentMeta } from './componentMeta';
import styles from './ChatControlsDrawer.module.css';

function Parameter({ name, label, value, min, max, step, onChange }: {
  name: string; label: string; value: number; min: number; max?: number; step: number; onChange: (value: number) => void;
}): ReactElement {
  const ref = useComponentMeta<HTMLInputElement>(chatComponentMeta(`chat.controls.${name}`));
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return <label className={styles.field}>{label}<input ref={ref} data-ui-id={`chat.controls.${name}`} type="number" aria-label={label}
    min={min} max={max} step={step} value={draft} onChange={event => {
      const raw = event.currentTarget.value;
      setDraft(raw);
      if (raw.trim() && Number.isFinite(Number(raw))) onChange(Number(raw));
    }} onBlur={() => setDraft(String(value))} /></label>;
}

export function ChatControlsDrawer({ children, selectionLabel, hideModelSettings = false }: { children: ReactNode; selectionLabel?: string; hideModelSettings?: boolean }): ReactElement {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadModelSettings);
  useEffect(() => {
    const refresh = (): void => setSettings(loadModelSettings());
    window.addEventListener(MODEL_SETTINGS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(MODEL_SETTINGS_CHANGED_EVENT, refresh);
  }, []);
  const presets = useMotionPresets();
  const startY = useRef<number | null>(null);
  const dragged = useRef(false);
  const drawerRef = useComponentMeta<HTMLElement>(chatComponentMeta('chat.controls.drawer'));
  const handleRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.controls.handle'));
  const backdropRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.controls.backdrop'));
  const thinkingRef = useComponentMeta<HTMLSelectElement>(chatComponentMeta('chat.controls.thinking'));
  const streamRef = useComponentMeta<HTMLInputElement>(chatComponentMeta('chat.controls.stream'));
  const close = useCallback(() => { setOpen(false); handleRef.current?.focus(); }, [handleRef]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'Tab') {
        const focusable = drawerRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]');
        const first = focusable?.[0]; const last = focusable?.[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, drawerRef, close]);
  function update(patch: Partial<ModelSettings>): void { setSettings(updateModelSettings(patch)); }
  return <>
    <AnimatePresence>{open ? <motion.button ref={backdropRef} className={styles.backdrop} type="button" aria-label="关闭会话参数菜单"
      data-ui-id="chat.controls.backdrop" variants={presets.sheetBackdrop} initial="hidden" animate="visible" exit="hidden" onClick={close} /> : null}</AnimatePresence>
    <motion.section ref={drawerRef} className={`${styles.drawer} ${open ? styles.open : ''}`} data-ui-id="chat.controls.drawer"
      data-open={open} role={open ? 'dialog' : undefined} aria-modal={open || undefined} aria-label="会话参数菜单"
      data-skin-surface="overlay"
      animate={{ height: open ? 'auto' : 44 }} transition={presets.reduced ? { duration: 0 } : { type: 'spring', ...sheetSpring }}>
      <button ref={handleRef} type="button" className={styles.handle} data-ui-id="chat.controls.handle"
        aria-label={open ? '收起会话菜单' : '展开会话菜单'} aria-expanded={open} onClick={() => { if (!dragged.current) setOpen(current => !current); dragged.current = false; }}
        onPointerDown={event => { dragged.current = false; startY.current = event.clientY; event.currentTarget.setPointerCapture?.(event.pointerId); }}
        onPointerUp={event => {
          if (startY.current !== null && Math.abs(event.clientY - startY.current) > 20) { dragged.current = true; setOpen(event.clientY < startY.current); }
          startY.current = null;
        }} onPointerCancel={() => { startY.current = null; }}>
        <span className={styles.grip} /><span className={styles.selection} title={selectionLabel}>{open ? '会话参数' : selectionLabel ?? '上拉调整 · 模型与语音'}</span><span aria-hidden="true">{open ? '⌄' : '⌃'}</span>
      </button>
      <div className={styles.content} hidden={!open}>
        {children}
        {!hideModelSettings ? <><div className={styles.grid}>
          <Parameter name="context" label="最大上下文" value={settings.maxContext} min={1} step={1} onChange={value => update({ maxContext: value })} />
          <Parameter name="output" label="最大输出 token" value={settings.maxOutputTokens} min={1} step={1} onChange={value => update({ maxOutputTokens: value })} />
          <Parameter name="temperature" label="温度" value={settings.temperature} min={0} max={2} step={0.05} onChange={value => update({ temperature: value })} />
          <Parameter name="topP" label="Top P" value={settings.topP} min={0} max={1} step={0.01} onChange={value => update({ topP: value })} />
        </div>
        <label className={styles.field}>思考等级<select ref={thinkingRef} data-ui-id="chat.controls.thinking" aria-label="思考等级" value={settings.thinkingLevel}
          onChange={event => update({ thinkingLevel: event.currentTarget.value as ModelSettings['thinkingLevel'] })}>
          <option value="off">关闭</option><option value="low">低</option><option value="medium">中</option><option value="high">高</option>
        </select></label>
        <label className={styles.switch}>流式输出<input ref={streamRef} data-ui-id="chat.controls.stream" type="checkbox" role="switch" aria-label="流式输出" checked={settings.stream} onChange={event => update({ stream: event.currentTarget.checked })} /></label>
        <p className={styles.hint}>参数自动保存，下次发送时生效</p></> : null}
      </div>
    </motion.section>
  </>;
}
